import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Sparkles, History, Heart, Users, Bookmark, CalendarDays } from "lucide-react";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import PublicLayout from "../components/PublicLayout";
import RecordingCard from "../components/RecordingCard";
import { Spinner, EmptyState } from "../components/states";
import { BackButton, Breadcrumbs, ExternalLink, useEscape } from "../lib/nav";

function Row({ icon: Icon, title, children, empty }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl font-bold uppercase flex items-center gap-2 mb-4"><Icon size={18} className="text-[#E8552A]" /> {title}</h2>
      {children || <EmptyState title={empty} />}
    </section>
  );
}

export default function UserDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tix, setTix] = useState(null);
  useEffect(() => { api.get("/user/dashboard").then((r) => { setD(r.data); setLoading(false); }).catch(() => setLoading(false)); api.get("/orders/mine").then((r) => setTix(r.data)).catch(() => {}); }, []);

  const grid = (items) => items?.length ? (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{items.map((r, i) => <RecordingCard key={r.id} rec={r} index={i} />)}</div>
  ) : null;

  return (
    <PublicLayout>
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6 py-8">
        <div className="flex items-center gap-4 flex-wrap mb-4"><BackButton fallback="/" /><Breadcrumbs items={[{ label: "Live", to: "/" }, { label: "Dashboard" }]} /></div>
        <p className="mono-label text-[#E8552A]">// LISTENER DASHBOARD</p>
        <h1 className="font-display text-4xl font-extrabold uppercase mt-2">Welcome, {user?.name?.split(" ")[0]}</h1>
        {tix && tix.tickets.length > 0 && (
          <Row icon={CalendarDays} title="My Tickets">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tix.tickets.map((t) => { const o = tix.orders.find((x) => x.id === t.order_id); return (
                <div key={t.id} data-testid={`ticket-${t.id}`} className="card-copper rounded-xl p-4 flex gap-4 items-center">
                  <img src={`${API}/tickets/${t.id}/qr.png`} alt="QR" className="w-24 h-24 rounded bg-white p-1" />
                  <div className="min-w-0"><p className="font-display font-semibold truncate">{o?.event_title}</p><p className="mono-label text-[#71717A]">{t.ticket_type} · {t.attendee_name}</p><p className={`mono-label mt-1 ${t.status === "used" ? "text-[#71717A]" : "text-[#10B981]"}`}>{t.status === "used" ? "CHECKED IN" : "VALID"}</p><p className="mono-label text-[#71717A] break-all text-[10px] mt-1">{t.token}</p>{o?.status === "paid" && <a data-testid={`invoice-${t.order_id}`} href={`${API}/orders/${t.order_id}/invoice.pdf`} target="_blank" rel="noreferrer" className="mono-label text-[#E8552A] hover:underline inline-block mt-1">INVOICE PDF ↗</a>}</div>
                </div>); })}
            </div>
          </Row>
        )}

        {loading ? <Spinner /> : !d ? <EmptyState title="Could not load dashboard" /> : (
          <>
            {d.live?.id && (
              <Link to="/" className="block card-copper rounded-xl p-5 mt-6 flex items-center gap-4">
                <span className="live-pulse w-3 h-3 rounded-full bg-[#10B981]" />
                <div><p className="mono-label text-[#10B981]">LIVE NOW</p><p className="font-display font-bold">{d.live.title}</p></div>
                <Radio className="ml-auto text-[#E8552A]" />
              </Link>
            )}
            <Row icon={Sparkles} title="Recommended for You" empty="No recommendations yet">{grid(d.recommended)}</Row>
            <Row icon={History} title="Recently Played" empty="Nothing played yet">{grid(d.recently_played)}</Row>
            <Row icon={Heart} title="Favorites" empty="No favorites — tap the heart on any show">{grid(d.favorites)}</Row>
            <Row icon={Bookmark} title="Saved Shows" empty="No saved shows yet">{grid(d.saved)}</Row>
            <Row icon={Users} title="Following" empty="You're not following anyone yet">
              {d.following?.length ? (
                <div className="flex flex-wrap gap-4">
                  {d.following.map((f) => (
                    <Link key={f.id} to="/artists" className="card-copper rounded-xl p-3 flex items-center gap-3 w-56">
                      <img src={f.profile_image} alt={f.name} className="w-10 h-10 rounded-full object-cover" />
                      <div><p className="font-display font-semibold text-sm truncate">{f.stage_name || f.name}</p><p className="mono-label text-[#71717A]">{(f.genres || [])[0]}</p></div>
                    </Link>
                  ))}
                </div>
              ) : null}
            </Row>
            <Row icon={CalendarDays} title="Upcoming Shows" empty="No upcoming events">
              {d.upcoming?.length ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {d.upcoming.map((e) => (
                    <Link key={e.id} to="/events" className="card-copper rounded-xl p-4">
                      <p className="font-display font-semibold">{e.title}</p>
                      <p className="mono-label text-[#E8552A] mt-1">{new Date(e.date).toLocaleDateString()}</p>
                      <p className="mono-label text-[#71717A]">{e.venue}</p>
                    </Link>
                  ))}
                </div>
              ) : null}
            </Row>
          </>
        )}
      </div>
    </PublicLayout>
  );
}
